import { Box, Button, Divider, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { ReactNode, useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { menuPropsModal } from "../../../../utils";
import { textFieldStyles } from "../../../quotes/quote-create-stepper/basic-quote/components/QuoteBasicRow";
import { AddressInformationFields } from "../../../shared/components/forms/adressInformation/AddressInformationFields";
import { ContactInformationFields } from "../../../shared/components/forms/contactInformation/ContactInformationFields";
import { colors } from "../../../ui/colors";
import { SASButtonStyles, SASSelectStyles } from "../../../ui/styles/inputStyles";

interface EmailEntry {
  value: string;
  tag?: string;
  isDefault?: boolean;
}

interface PhoneEntry {
  countryCode: string;
  dialCode: string;
  phone: string;
  tag?: string;
  isDefault?: boolean;
}

interface WorkforceFormValues {
  firstName: string;
  lastName: string;
  workforceRole: string;
  ratePerHour: string | number;
  emails: { value: string }[];
  phones: { countryCode: string; dialCode: string; phone: string }[];
  address: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  status?: string;
}

interface WorkforceDefaultValues extends Partial<WorkforceFormValues> {
  status?: string;
  emails?: EmailEntry[];
  phones?: PhoneEntry[];
}

interface ContactState {
  emails: EmailEntry[];
  phones: PhoneEntry[];
  isValid: boolean;
}

interface StepWorkforceFormProps {
  onNext: (data: WorkforceFormValues & { emails: EmailEntry[]; phones: PhoneEntry[] }) => void;
  defaultValues?: WorkforceDefaultValues;
}

export const StepWorkforceForm = ({ onNext, defaultValues = {} }: StepWorkforceFormProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    unregister,
    control,
    formState: { errors },
  } = useForm<WorkforceFormValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
      workforceRole: "",
      ratePerHour: "",
      emails: [{ value: "" }],
      phones: [{ countryCode: "US", dialCode: "+1", phone: "" }],
      address: "",
      city: "",
      state: "",
      country: "",
      zipCode: "",
      ...defaultValues,
    },
  });

  const [contactState, setContactState] = useState<ContactState>({ emails: [], phones: [], isValid: true });
  const [showContactErrors, setShowContactErrors] = useState(false);
  const handleContactInfoChange = useCallback((state: ContactState) => {
    setContactState(state);
  }, []);

  const isApplicant = defaultValues?.status === "APPLICANT";

  const employmentTypes = [
    { id: 1, label: "supervisor" },
    { id: 2, label: "contractor" },
    { id: 3, label: "employee" },
  ];

  const Label = ({ text }: { text: string }) => (
    <Typography
      variant="subtitle2"
      sx={{
        color: colors.gray[600],
        fontWeight: 600,
        mb: 1,
      }}
    >
      {text}
    </Typography>
  );

  const SectionTitle = ({ children, icon = true }: { children: ReactNode; icon?: boolean }) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        {icon && (
          <Box
            sx={{
              width: 4,
              height: 24,
              bgcolor: colors.orange.base,
              borderRadius: 2,
              mr: 2,
            }}
          />
        )}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: colors.gray[800],
            letterSpacing: 0.5,
          }}
        >
          {children}
        </Typography>
      </Box>
      <Divider sx={{ borderColor: colors.gray[200], opacity: 0.8 }} />
    </Box>
  );

  const onSubmit = (data: WorkforceFormValues) => {
    if (!contactState.isValid) {
      setShowContactErrors(true);
      return;
    }
    onNext({
      ...data,
      status: isApplicant ? "APPLICANT" : data.status || "ACTIVE",
      emails: contactState.emails,
      phones: contactState.phones,
    });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        width: "90%",
        gap: 5,
        py: 3,
      }}
    >
      {/* === Employee / Applicant Information === */}
      <Box sx={{ width: "100%" }}>
        <SectionTitle>{isApplicant ? "Applicant Information" : "Employee Information"}</SectionTitle>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Label text="First Name" />
            <TextField
              fullWidth
              placeholder="First name"
              {...register("firstName", { required: "First name is required" })}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              sx={textFieldStyles}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label text="Last Name" />
            <TextField
              fullWidth
              placeholder="Last name"
              {...register("lastName", { required: "Last name is required" })}
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              sx={textFieldStyles}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Label text="Employment Type" />
            <FormControl fullWidth error={!!errors.workforceRole} sx={SASSelectStyles}>
              <InputLabel>Employment Type</InputLabel>

              <Controller
                name="workforceRole"
                control={control}
                rules={{ required: "Employment type is required" }}
                render={({ field }) => (
                  <Select {...field} label="Employment Type" MenuProps={menuPropsModal}>
                    {employmentTypes.map((option) => (
                      <MenuItem key={option.id} value={option.label}>
                        {option.label.charAt(0).toUpperCase() + option.label.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />

              {errors.workforceRole && <FormHelperText>{errors.workforceRole.message}</FormHelperText>}
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Address Information */}
      <AddressInformationFields control={control} register={register} errors={errors} setValue={setValue} getCoords={false} />

      {/* === Financial Information === */}
      {!isApplicant && (
        <Box sx={{ width: "100%" }}>
          <SectionTitle>Financial</SectionTitle>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Label text="Rate per Hour (USD)" />
              <TextField
                fullWidth
                type="number"
                placeholder="Enter rate per hour"
                inputProps={{ step: "0.01", min: 0 }}
                {...register("ratePerHour", {
                  required: !isApplicant ? "Rate per hour is required" : false,
                  min: { value: 0, message: "Value must be positive" },
                  valueAsNumber: true,
                })}
                error={!!errors.ratePerHour}
                helperText={errors.ratePerHour?.message}
                sx={textFieldStyles}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* === Contact Information === */}
      <ContactInformationFields
        key="workforce-contact-info"
        initialEmails={defaultValues.emails || []}
        initialPhones={defaultValues.phones || []}
        onChange={handleContactInfoChange}
        requireEither={true}
        showErrors={showContactErrors}
      />

      {/* === Submit === */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Button type="submit" sx={SASButtonStyles}>
          Continue
        </Button>
      </Box>
    </Box>
  );
};
